import React, { useState, useEffect, useRef, useMemo } from "react";
import { db } from "../../firebase/firebaseConfig";
import { collection, addDoc, query, where, getDocs, doc, getDoc, updateDoc } from 'firebase/firestore';
import LevelingSystem from '../SRS/LevelingSystem';
import StudyModeCodeEditor from '../StudyMode/StudyModeCodeEditor';
import { fetchCookbookSaveQuota, fetchManualSnippetCreationQuota, messageForCookbookQuotaCheckUnavailable, messageSnippetCreationMonthlyLimitReached, messageForSnippetQuotaCheckUnavailable } from '../../services/subscriptionQuotaService';
import {
  confirmCookbookDeckNotRemovedByQuotaTrigger,
  cookbookDeckRemovedByMonthlyLimitMessage,
} from '../../utils/cookbookDeckQuotaTriggerWatch';
import { ReactComponent as DeckIcon } from '../../Icons/deck-icon.svg';
import { ReactComponent as BookIcon } from '../../Icons/book.svg';
import { ReactComponent as BookShelfIcon } from '../../Icons/book-shelf.svg';
import { ReactComponent as FullPageCreatorRailCodeIcon } from '../../Icons/snippet-icon.svg';
import { ReactComponent as FullPageCreatorRailNotesIcon } from '../../Icons/graduation-cap-icon.svg';
import IconRenderer from '../ui/IconRenderer';
import "./UserCreation.css";

/** Brand accent for language dropdown / Monaco toolbar badge */
function getLanguageBrandDot(langName) {
  const map = {
    JavaScript: '#f7df1e',
    TypeScript: '#3178c6',
    Python: '#3776ab',
    Java: '#f89820',
    C: '#a8b9cc',
    'C++': '#00599c',
    'C#': '#9b4f96',
    Kotlin: '#7f52ff',
    Ruby: '#cc342d',
    PHP: '#777bb4',
    Swift: '#f05138',
    Go: '#00add8',
    Rust: '#dea584',
  };
  return map[langName] || '#94a3b8';
}

/**
 * Normalize a variety of timestamp shapes (Firestore Timestamp, ISO string,
 * millis number, Date) into a comparable millisecond value. Returns 0 when
 * the value is missing or unparseable.
 */
function timestampToMillis(raw) {
  if (raw == null) return 0;
  if (typeof raw === 'number') return raw;
  if (typeof raw === 'string') {
    const parsed = Date.parse(raw);
    return Number.isFinite(parsed) ? parsed : 0;
  }
  if (raw instanceof Date) return raw.getTime();
  if (typeof raw.toMillis === 'function') return raw.toMillis();
  if (typeof raw.toDate === 'function') return raw.toDate().getTime();
  if (typeof raw.seconds === 'number') {
    return raw.seconds * 1000 + Math.floor((raw.nanoseconds || 0) / 1e6);
  }
  return 0;
}

/**
 * A "track cookbook" is one auto-generated from a roadmap track node (see
 * `services/roadmapService.js`). These exist to back roadmap study sessions
 * and shouldn't be selectable as a destination when manually creating a
 * snippet — picking one would mix user-authored cards into a curriculum-
 * owned cookbook. We detect them by the dedicated roadmap metadata fields,
 * with a tag-based fallback for any legacy docs missing those fields.
 */
function isTrackCookbook(cookbook) {
  if (!cookbook) return false;
  if (cookbook.roadmapTrackId) return true;
  if (cookbook.roadmapNodeId) return true;
  if (cookbook.trackId) return true;
  const tags = Array.isArray(cookbook.tags) ? cookbook.tags : [];
  return tags.some(tag => typeof tag === 'string' && tag.toLowerCase() === 'roadmap');
}

/**
 * Cookbook recency score used to sort the picker. We take the most recent of
 * lastAccessed / updatedAt / createdAt so a cookbook the user just edited or
 * studied bubbles to the top — matching the user's intuition of "recent",
 * rather than strictly when the doc was first inserted.
 */
function getCookbookRecencyMillis(cookbook) {
  if (!cookbook) return 0;
  const candidates = [
    cookbook.lastAccessed,
    cookbook.lastAccessedAt,
    cookbook.updatedAt,
    cookbook.updated_at,
    cookbook.createdAt,
    cookbook.created_at,
    cookbook.createdOn,
  ];
  let best = 0;
  for (const raw of candidates) {
    const ms = timestampToMillis(raw);
    if (ms > best) best = ms;
  }
  return best;
}

function getSnippetFilename(langName) {
  const raw = String(langName || '').trim();
  const lower = raw.toLowerCase();
  if (lower.includes('typescript')) return 'main.ts';
  if (lower.includes('javascript')) return 'main.js';
  if (lower.includes('python')) return 'main.py';
  if (lower === 'java') return 'Main.java';
  if (lower === 'c++' || lower.includes('c++')) return 'main.cpp';
  if (lower === 'c') return 'main.c';
  if (lower.includes('ruby')) return 'main.rb';
  if (lower.includes('php')) return 'main.php';
  if (lower.includes('swift')) return 'main.swift';
  if (lower === 'go' || lower.includes('golang')) return 'main.go';
  if (lower.includes('rust')) return 'main.rs';
  return 'snippet.txt';
}

function FullPageCreator({ userId, previousView, onClose }) {

  // Default to snippet creation with code editor displayed
  const [creationType, setCreationType] = useState('snippet');
  const [codeContent, setCodeContent] = useState('');
  const [notesContent, setNotesContent] = useState('');
  const [editorMode, setEditorMode] = useState('code'); // 'code' or 'notes'
  const [language, setLanguage] = useState('JavaScript');
  const [step, setStep] = useState('code'); // Start with code editor for snippets
  const [showLanguageDropdown, setShowLanguageDropdown] = useState(false);
  const [snippetEditorExpanded, setSnippetEditorExpanded] = useState(false);
  const [levelingSystem] = useState(new LevelingSystem(userId));

  const snippetToolbarFilename = useMemo(() => getSnippetFilename(language), [language]);
  const languageBrandDot = useMemo(() => getLanguageBrandDot(language), [language]);

  // Check for localStorage values on component mount (legacy support, can be removed later)
  useEffect(() => {
    const storedLanguage = localStorage.getItem('selectedLanguage');

    if (storedLanguage) {
      setLanguage(storedLanguage);
      localStorage.removeItem('selectedLanguage');
    }
  }, []);

  useEffect(() => {
    if (editorMode !== 'code') {
      setSnippetEditorExpanded(false);
    }
  }, [editorMode]);

  // Snippet specific states
  const [snippetTitle, setSnippetTitle] = useState("");
  const [snippetDescription, setSnippetDescription] = useState("");
  const [snippetTags, setSnippetTags] = useState("");
  const [selectedCookbook, setSelectedCookbook] = useState("");
  const [cookbooks, setCookbooks] = useState([]);
  const [showCookbookSelect, setShowCookbookSelect] = useState(false);
  const [showCookbookDropdown, setShowCookbookDropdown] = useState(false);
  const [cookbookSearchQuery, setCookbookSearchQuery] = useState("");
  const cookbookDropdownRef = useRef(null);
  const cookbookSearchInputRef = useRef(null);

  // Cookbook/Collection specific states
  const [newItemName, setNewItemName] = useState("");
  const [errorMessage, setErrorMessage] = useState("");

  const languages = [
    "JavaScript", "TypeScript", "Python", "Java", "C", "C++", "C#",
    "Kotlin", "Ruby", "PHP", "Swift", "Go", "Rust"
  ];

  // Load cookbooks for snippet creation
  useEffect(() => {
    if (creationType === 'snippet') {
      loadCookbooks();
    }
  }, [userId, creationType]);

  // Close cookbook dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (cookbookDropdownRef.current && !cookbookDropdownRef.current.contains(event.target)) {
        setShowCookbookDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Reset search and auto-focus the search input whenever the cookbook
  // dropdown opens; clear the query when it closes so the next open starts
  // from a fresh, unfiltered list.
  useEffect(() => {
    if (showCookbookDropdown) {
      setCookbookSearchQuery('');
      // Defer to next tick so the input has mounted before we focus it.
      const focusTimer = setTimeout(() => {
        cookbookSearchInputRef.current?.focus?.();
      }, 0);
      return () => clearTimeout(focusTimer);
    }
    setCookbookSearchQuery('');
    return undefined;
  }, [showCookbookDropdown]);

  // Filter the loaded cookbook list by the search query (case-insensitive
  // substring match against name and language). When the query is empty the
  // full list is returned, preserving the most-recently-created sort.
  const filteredCookbooks = useMemo(() => {
    const query = cookbookSearchQuery.trim().toLowerCase();
    if (!query) return cookbooks;
    return cookbooks.filter(cookbook => {
      const name = (cookbook.name || '').toLowerCase();
      const language = (cookbook.language || '').toLowerCase();
      return name.includes(query) || language.includes(query);
    });
  }, [cookbooks, cookbookSearchQuery]);

  const loadCookbooks = async () => {
    try {
      console.log(`Loading cookbooks for user: ${userId}`);
      const decksRef = collection(db, `users/${userId}/decks`);
      const snapshot = await getDocs(decksRef);
      const loadedCookbooks = snapshot.docs
        .map(doc => ({
          id: doc.id,
          ...doc.data()
        }))
        .filter(deck => deck.type === 'cookbook' || !deck.type) // Only cookbooks
        .filter(deck => !isTrackCookbook(deck)) // Exclude roadmap track cookbooks
        .sort((a, b) => getCookbookRecencyMillis(b) - getCookbookRecencyMillis(a)); // Most recent activity first

      console.log(`Found ${loadedCookbooks.length} cookbooks`);
      setCookbooks(loadedCookbooks);
      setShowCookbookSelect(true);
    } catch (error) {
      console.error('Error loading cookbooks:', error);
    }
  };

  // Handle creation type change
  const handleTypeChange = (type) => {
    setCreationType(type);

    // Adjust the step based on the selected type
    if (type === 'snippet') {
      setStep('code');
    } else {
      setStep('metadata');
    }
  };

  // Handle next step for snippets
  const handleNext = () => {
    if (creationType === 'snippet' && codeContent.trim()) {
      setStep('metadata');
    }
  };

  const resetForm = () => {
    setSnippetTitle("");
    setSnippetDescription("");
    setSnippetTags("");
    setNewItemName("");
    setCodeContent("");
  };

  // Create new cookbook
  const createCookbook = async () => {
    // Clear any previous error messages
    setErrorMessage("");

    if (!userId) {
      setErrorMessage("You must be signed in to create a cookbook.");
      return;
    }

    if (!newItemName.trim()) {
      setErrorMessage("Please enter a name for your cookbook");
      return;
    }

    try {
      const quota = await fetchCookbookSaveQuota();
      if (quota.quotaCheckUnavailable) {
        setErrorMessage(messageForCookbookQuotaCheckUnavailable(quota));
        return;
      }
      if (!quota.isPremium && quota.allowed === false) {
        setErrorMessage(
          `Free accounts can create up to ${quota.limit} new cookbooks per month (UTC). Upgrade to Pro for unlimited cookbooks, AI, and full roadmaps.`
        );
        return;
      }

      const timestamp = new Date().toISOString();
      const levelingFields = levelingSystem.initializeCookbookLeveling();

      const newDeck = {
        name: newItemName.trim(),
        description: "",
        language: language,
        type: "cookbook",
        tags: snippetTags.split(',').map(tag => tag.trim()).filter(tag => tag),
        createdAt: timestamp,
        updatedAt: timestamp,
        lastAccessed: timestamp,
        userId: userId,
        ...levelingFields // Add leveling fields
      };

      const decksRef = collection(db, `users/${userId}/decks`);
      const deckRef = await addDoc(decksRef, newDeck);
      if (!quota.isPremium) {
        const outcome = await confirmCookbookDeckNotRemovedByQuotaTrigger(deckRef);
        if (!outcome.ok && outcome.removedByMonthlyLimit) {
          setErrorMessage(cookbookDeckRemovedByMonthlyLimitMessage);
          return;
        }
      }
      onClose();
    } catch (error) {
      console.error("Error creating cookbook:", error);
      const code = error?.code || '';
      const msg = error?.message || '';
      if (code === 'functions/not-found' || msg.includes('NOT_FOUND')) {
        setErrorMessage(
          'The subscription check service is not available. Deploy Cloud Functions (validateCookbookSave) or try again later.'
        );
      } else if (code === 'functions/internal' || code === 'internal') {
        setErrorMessage(
          'Could not verify account limits (server error). Confirm functions are deployed and try again. If you use the Firebase emulator, connect the app to the functions emulator.'
        );
      } else if (code === 'permission-denied') {
        setErrorMessage('You do not have permission to create a cookbook.');
      } else {
        setErrorMessage(msg || 'Failed to create cookbook. Please try again.');
      }
    }
  };

  // Create new snippet (as a card in a cookbook)
  const createSnippet = async () => {
    // Clear any previous error messages
    setErrorMessage("");

    if (!snippetTitle.trim() || !snippetDescription.trim() || !codeContent.trim()) {
      setErrorMessage("Please fill in all required fields (title, description, and code)");
      return;
    }

    if (!selectedCookbook) {
      setErrorMessage("Please select a cookbook to add this snippet to");
      return;
    }

    try {
      const quota = await fetchManualSnippetCreationQuota();
      if (quota.quotaCheckUnavailable) {
        setErrorMessage(messageForSnippetQuotaCheckUnavailable(quota));
        return;
      }
      if (!quota.isPremium && quota.allowed === false) {
        setErrorMessage(messageSnippetCreationMonthlyLimitReached(quota));
        return;
      }

      const timestamp = new Date().toISOString();
      const levelingFields = levelingSystem.initializeSnippetLeveling();

      // Create the card directly in the selected cookbook
      const cardsRef = collection(db, `users/${userId}/decks/${selectedCookbook}/cards`);

      const newCard = {
        title: snippetTitle.trim(),
        description: snippetDescription.trim(),
        solution: codeContent.trim(),
        notes: notesContent.trim(),
        language: language,
        creationSource: 'manual',
        tags: snippetTags.split(',').map(tag => tag.trim()).filter(tag => tag),
        createdAt: timestamp,
        updatedAt: timestamp,
        ...levelingFields
      };

      console.log("Creating new card in cookbook:", selectedCookbook, newCard);

      try {
        const docRef = await addDoc(cardsRef, newCard);

        console.log("Card successfully created with ID:", docRef.id);

        // Verify the card was created
        const createdCardRef = doc(db, `users/${userId}/decks/${selectedCookbook}/cards/${docRef.id}`);
        const createdCardDoc = await getDoc(createdCardRef);

        if (createdCardDoc.exists()) {
          console.log("Verified card exists in Firestore:", createdCardDoc.data());
          onClose();
        } else {
          console.error("Card was not found in Firestore after creation");
          setErrorMessage("Error saving card to database. Please try again.");
        }
      } catch (writeError) {
        console.error("Error writing card to Firestore:", writeError);
        setErrorMessage("Error saving card to database: " + writeError.message);
      }
    } catch (error) {
      console.error("Error creating card:", error);
      setErrorMessage("Error creating card: " + error.message);
    }
  };

  // Create new collection
  const createCollection = async () => {
    // Clear any previous error messages
    setErrorMessage("");

    if (!newItemName.trim()) {
      setErrorMessage("Please enter a name for your collection");
      return;
    }

    try {
      const timestamp = new Date().toISOString();
      const newCollection = {
        name: newItemName.trim(),
        description: "",
        tags: snippetTags.split(',').map(tag => tag.trim()).filter(tag => tag),
        createdAt: timestamp,
        updatedAt: timestamp,
        deckIds: [],
        userId: userId
      };

      const collectionsRef = collection(db, `users/${userId}/collections`);
      await addDoc(collectionsRef, newCollection);
      onClose();
    } catch (error) {
      console.error("Error creating collection:", error);
    }
  };

  const handleSubmit = () => {
    if (creationType === 'snippet') {
      createSnippet();
    } else if (creationType === 'cookbook') {
      createCookbook();
    } else {
      createCollection();
    }
  };

  const snippetDetailsComplete =
    creationType === 'snippet'
    && step === 'metadata'
    && Boolean(snippetTitle.trim())
    && Boolean(snippetDescription.trim())
    && Boolean(codeContent.trim());

  const cookbookTargetChosen = Boolean(selectedCookbook);

  const focusCookbookTrigger = () => {
    setTimeout(() => {
      document.getElementById('full-page-creator-cookbook-trigger')?.focus?.();
    }, 0);
  };

  const renderFlashcardCreationChecklist = ({ afterChecklistTrack = null } = {}) => {
    const snippetSnippetDone = snippetDetailsComplete;
    const snippetCookbookDone = creationType === 'snippet' && cookbookTargetChosen;
    const cookbookNeedsAttention =
      creationType === 'snippet'
      && snippetSnippetDone
      && !cookbookTargetChosen;

    return (
      <nav className="full-page-creator-creation-checklist" aria-label="Creation progress">
        <ol className="full-page-creator-creation-checklist-track">
          <li className="full-page-creator-creation-checklist-item">
            <button
              type="button"
              className={[
                'full-page-creator-creation-checklist-pill',
                creationType === 'snippet' && step === 'code' ? 'full-page-creator-creation-checklist-pill--current' : '',
                snippetSnippetDone ? 'full-page-creator-creation-checklist-pill--done' : '',
                creationType !== 'snippet' ? 'full-page-creator-creation-checklist-pill--idle' : '',
              ].filter(Boolean).join(' ')}
              onClick={() => {
                handleTypeChange('snippet');
                setStep('code');
              }}
            >
              <span className="full-page-creator-creation-checklist-badge" aria-hidden="true">
                {snippetSnippetDone ? '✓' : '1'}
              </span>
              <DeckIcon className="full-page-creator-creation-checklist-icon" aria-hidden />
              <span className="full-page-creator-creation-checklist-label">Snippet</span>
            </button>
          </li>
          <li className="full-page-creator-creation-checklist-connector" aria-hidden="true" />
          <li className="full-page-creator-creation-checklist-item">
            <button
              type="button"
              className={[
                'full-page-creator-creation-checklist-pill',
                creationType === 'cookbook' ? 'full-page-creator-creation-checklist-pill--mode' : '',
                creationType === 'snippet' && step === 'metadata' ? 'full-page-creator-creation-checklist-pill--current' : '',
                snippetCookbookDone ? 'full-page-creator-creation-checklist-pill--done' : '',
                cookbookNeedsAttention ? 'full-page-creator-creation-checklist-pill--prompt' : '',
              ].filter(Boolean).join(' ')}
              onClick={() => {
                if (creationType === 'cookbook') return;
                if (creationType !== 'snippet') {
                  setCreationType('snippet');
                }
                setStep('metadata');
                focusCookbookTrigger();
              }}
            >
              <span className="full-page-creator-creation-checklist-badge" aria-hidden="true">
                {snippetCookbookDone ? '✓' : '2'}
              </span>
              <BookIcon className="full-page-creator-creation-checklist-icon" aria-hidden />
              <span className="full-page-creator-creation-checklist-label">Cookbook</span>
            </button>
          </li>
          <li
            className="full-page-creator-creation-checklist-connector full-page-creator-creation-checklist-connector--optional"
            aria-hidden="true"
          />
          <li className="full-page-creator-creation-checklist-item">
            <button
              type="button"
              className={[
                'full-page-creator-creation-checklist-pill',
                creationType === 'collection' ? 'full-page-creator-creation-checklist-pill--mode' : '',
              ].filter(Boolean).join(' ')}
              onClick={() => handleTypeChange('collection')}
            >
              <span
                className="full-page-creator-creation-checklist-badge full-page-creator-creation-checklist-badge--neutral"
                aria-hidden="true"
              >
                3
              </span>
              <BookShelfIcon className="full-page-creator-creation-checklist-icon" aria-hidden />
              <span className="full-page-creator-creation-checklist-label">Collection</span>
            </button>
          </li>
        </ol>
        <div className="full-page-creator-creation-checklist-meta-row">
          {afterChecklistTrack}
          <div className="full-page-creator-creation-checklist-meta">
            {cookbookNeedsAttention && (
              <p className="full-page-creator-creation-checklist-hint" role="status">
                Snippet is ready. Choose a cookbook for this card below.
              </p>
            )}
            {creationType !== 'cookbook' && (
              <button
                type="button"
                className="full-page-creator-creation-checklist-pill full-page-creator-creation-checklist-pill--meta-action"
                onClick={() => handleTypeChange('cookbook')}
              >
                <span
                  className="full-page-creator-creation-checklist-badge full-page-creator-creation-checklist-badge--meta-plus"
                  aria-hidden="true"
                >
                  <svg
                    className="full-page-creator-creation-checklist-badge-plus-glyph"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M12 5v14M5 12h14"
                      stroke="currentColor"
                      strokeWidth="2.75"
                      strokeLinecap="round"
                    />
                  </svg>
                </span>
                <BookIcon className="full-page-creator-creation-checklist-icon" aria-hidden />
                <span className="full-page-creator-creation-checklist-label">Create Cookbook</span>
              </button>
            )}
          </div>
        </div>
      </nav>
    );
  };

  /** Cookbook target picker — metadata step */
  const renderSnippetCookbookFields = () => (
    showCookbookSelect ? (
      <div className="form-group full-page-creator-cookbook-selector" ref={cookbookDropdownRef}>
        <label htmlFor="full-page-creator-cookbook-trigger">Cookbook: <span className="required-field">*</span></label>
        <div className="full-page-creator-cookbook-trigger-wrapper">
          <div
            id="full-page-creator-cookbook-trigger"
            className={`full-page-creator-cookbook-trigger ${showCookbookDropdown ? 'open' : ''} ${!selectedCookbook && !showCookbookDropdown ? 'full-page-creator-cookbook-trigger--placeholder' : ''}`}
            onClick={() => {
              // The arrow handles closing; the outer trigger only opens. This
              // prevents an outside-click → re-open flicker when the user
              // clicks inside the already-open trigger.
              if (!showCookbookDropdown) setShowCookbookDropdown(true);
            }}
            role="combobox"
            aria-expanded={showCookbookDropdown}
            aria-haspopup="listbox"
            aria-label="Select a cookbook"
          >
            {showCookbookDropdown ? (
              <input
                ref={cookbookSearchInputRef}
                type="text"
                className="full-page-creator-cookbook-trigger-search-input"
                value={cookbookSearchQuery}
                onChange={(e) => setCookbookSearchQuery(e.target.value)}
                onClick={(e) => e.stopPropagation()}
                onKeyDown={(e) => {
                  if (e.key === 'Escape') {
                    e.stopPropagation();
                    setShowCookbookDropdown(false);
                  }
                }}
                placeholder="Search cookbooks..."
                aria-label="Search cookbooks"
                autoComplete="off"
              />
            ) : (
              <span className="full-page-creator-cookbook-trigger-text">
                {selectedCookbook
                  ? (() => {
                    const chosen = cookbooks.find(c => c.id === selectedCookbook);
                    return chosen
                      ? `${chosen.name}${chosen.language ? ` (${chosen.language})` : ''}`
                      : 'Select a cookbook...';
                  })()
                  : 'Select a cookbook...'}
              </span>
            )}
            <span
              className="full-page-creator-cookbook-trigger-arrow"
              onClick={(e) => {
                e.stopPropagation();
                setShowCookbookDropdown(!showCookbookDropdown);
              }}
              role="button"
              aria-label={showCookbookDropdown ? 'Close cookbook list' : 'Open cookbook list'}
              tabIndex={-1}
            >▼</span>
          </div>
          {showCookbookDropdown && (
            <div
              className="full-page-creator-cookbook-dropdown"
              role="listbox"
              aria-label="Cookbook options"
            >
              {cookbooks.length === 0 ? (
                <div className="full-page-creator-cookbook-option full-page-creator-cookbook-option--empty">
                  No cookbooks yet. Use Create Cookbook in the checklist row above, then pick it here.
                </div>
              ) : filteredCookbooks.length === 0 ? (
                <div className="full-page-creator-cookbook-option full-page-creator-cookbook-option--empty">
                  No cookbooks match "{cookbookSearchQuery}".
                </div>
              ) : (
                filteredCookbooks.map(cookbook => (
                  <div
                    key={cookbook.id}
                    role="option"
                    aria-selected={selectedCookbook === cookbook.id}
                    className={`full-page-creator-cookbook-option ${selectedCookbook === cookbook.id ? 'selected' : ''}`}
                    onClick={() => {
                      setSelectedCookbook(cookbook.id);
                      setShowCookbookDropdown(false);
                    }}
                  >
                    <IconRenderer
                      name={cookbook.language ? cookbook.language.toLowerCase() : 'javascript'}
                      type="language"
                      size="small"
                      className="full-page-creator-cookbook-option-icon"
                    />
                    <span className="full-page-creator-cookbook-option-label">
                      {cookbook.name}
                    </span>
                    {cookbook.language && (
                      <span className="full-page-creator-cookbook-option-badge">
                        {cookbook.language}
                      </span>
                    )}
                  </div>
                ))
              )}
            </div>
          )}
        </div>
      </div>
    ) : null
  );

  const renderSnippetForm = ({ hideFormTitle = false } = {}) => (
    <div className="snippet-form full-page-form full-page-form--flashcard-embedded">
      {!hideFormTitle && (
        <h2 className="form-title">
          <DeckIcon className="form-title-icon" />
          Create New Snippet
        </h2>
      )}
      <div className="form-group">
        <label htmlFor="snippetTitle">Title: <span className="required-field">*</span></label>
        <input
          type="text"
          id="snippetTitle"
          value={snippetTitle}
          onChange={(e) => setSnippetTitle(e.target.value)}
          placeholder="e.g., Python List Comprehension"
          className="divider-input"
        />
      </div>

      <div className="form-group">
        <label htmlFor="snippetDescription">Description: <span className="required-field">*</span></label>
        <textarea
          id="snippetDescription"
          value={snippetDescription}
          onChange={(e) => setSnippetDescription(e.target.value)}
          placeholder="Explain the concept or provide instructions..."
          className="divider-input"
          rows="3"
        />
      </div>

      {renderSnippetCookbookFields()}

      <div className="form-group">
        <label htmlFor="snippetTags">Tags (comma-separated):</label>
        <input
          type="text"
          id="snippetTags"
          value={snippetTags}
          onChange={(e) => setSnippetTags(e.target.value)}
          placeholder="e.g., arrays, loops, algorithms"
          className="divider-input"
        />
      </div>

      {errorMessage && (
        <div className="error-message" role="alert">
          {errorMessage}
        </div>
      )}

      <div className="form-action-center">
        <button onClick={handleSubmit} className="submit-create-button">
          Create Snippet
        </button>
      </div>
    </div>
  );

  const renderCookbookCollectionForm = ({ hideFormTitle = false } = {}) => {
    const title = creationType === "cookbook" ? "Create New Cookbook" : "Create New Collection";
    const Icon = creationType === "cookbook" ? BookIcon : BookShelfIcon;

    return (
      <div className="create-item full-page-form full-page-form--flashcard-embedded">
        {!hideFormTitle && (
          <h2 className="form-title">
            <Icon className="form-title-icon" />
            {title}
          </h2>
        )}

        <div className="form-group">
          <label htmlFor="itemName">Name: <span className="required-field">*</span></label>
          <input
            type="text"
            id="itemName"
            value={newItemName}
            onChange={(e) => setNewItemName(e.target.value)}
            placeholder={`New ${creationType} Name`}
            className="divider-input"
          />
        </div>

        {creationType === "cookbook" && (
          <div className="language-selector">
            <label>Language:</label>
            <div className="cookbook-language-selector-container">
              <div
                className={`cookbook-selected-language ${showLanguageDropdown ? 'open' : ''}`}
                onClick={() => setShowLanguageDropdown(!showLanguageDropdown)}
              >
                <div className="full-page-creator-selected-language">
                  <IconRenderer
                    name={language.toLowerCase()}
                    type="language"
                    size="small"
                    className="language-icon"
                  />
                  <span>{language}</span>
                </div>
                <span className="dropdown-arrow">▼</span>
              </div>
              {showLanguageDropdown && (
                <div className="cookbook-language-dropdown">
                  {languages.map(lang => (
                    <div
                      key={lang}
                      className={`cookbook-language-option ${language === lang ? 'selected' : ''}`}
                      onClick={() => {
                        setLanguage(lang);
                        setShowLanguageDropdown(false);
                      }}
                    >
                      <IconRenderer
                        name={lang.toLowerCase()}
                        type="language"
                        size="small"
                        className="language-icon"
                      />
                      <span>{lang}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        <div className="form-group">
          <label htmlFor="itemTags">Tags (comma-separated):</label>
          <input
            type="text"
            id="itemTags"
            value={snippetTags}
            onChange={(e) => setSnippetTags(e.target.value)}
            placeholder="e.g., algorithms, data-structures"
            className="divider-input"
          />
        </div>

        {errorMessage && (
        <div className="error-message" role="alert">
          {errorMessage}
        </div>
      )}

        <div className="form-action-center">
          <button
            onClick={handleSubmit}
            className="submit-create-button"
          >
            Create {creationType}
          </button>
        </div>
      </div>
    );
  };

  const metadataFlashcardCopy = () => {
    if (creationType === 'snippet') {
      return {
        Icon: DeckIcon,
        title: 'Snippet details',
        subtitle: 'Add title, description, tags, and confirm the cookbook for this card.',
      };
    }
    if (creationType === 'cookbook') {
      return {
        Icon: BookIcon,
        title: 'New cookbook',
        subtitle: 'Name your cookbook, set its language, and add optional tags.',
      };
    }
    return {
      Icon: BookShelfIcon,
      title: 'New collection',
      subtitle: 'Name your collection and add optional tags.',
    };
  };

  const renderFlashcardLanguageDropdown = () => (
    <div className="language-selector full-page-creator-flashcard-language">
      <div className="language-selector-container">
        <div
          className={`selected-language ${showLanguageDropdown ? 'open' : ''}`}
          onClick={() => setShowLanguageDropdown(!showLanguageDropdown)}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              setShowLanguageDropdown(!showLanguageDropdown);
            }
          }}
          aria-expanded={showLanguageDropdown}
          aria-label="Editor language"
        >
          <div className="full-page-creator-selected-language">
            <IconRenderer
              name={language.toLowerCase()}
              type="language"
              size="small"
              className="full-page-creator-flashcard-language-icon"
            />
            <span>{language}</span>
          </div>
          <span className="dropdown-arrow">▼</span>
        </div>
        {showLanguageDropdown && (
          <div className="language-dropdown" role="listbox" aria-label="Language options">
            {languages.map(lang => (
              <div
                key={lang}
                role="option"
                aria-selected={language === lang}
                className={`language-option ${language === lang ? 'selected' : ''}`}
                onClick={() => {
                  setLanguage(lang);
                  setShowLanguageDropdown(false);
                }}
              >
                <IconRenderer
                  name={lang.toLowerCase()}
                  type="language"
                  size="small"
                  className="full-page-creator-flashcard-language-icon full-page-creator-flashcard-language-icon--option"
                />
                <span className="full-page-creator-language-option-label">{lang}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );

  const showSnippetEditorCard = creationType === 'snippet' && step === 'code';
  const snippetEditorLayoutExpanded =
    Boolean(snippetEditorExpanded && showSnippetEditorCard && editorMode === 'code');
  const metaCopy = metadataFlashcardCopy();
  const MetadataIcon = metaCopy.Icon;

  return (
    <div
      className={[
        'full-page-creator',
        snippetEditorLayoutExpanded ? 'full-page-creator--editor-expanded' : '',
      ].filter(Boolean).join(' ')}
    >
      <div
        className={[
          'creator-content',
          showSnippetEditorCard ? 'creator-content--flashcard-editor' : 'creator-content--flashcard-form',
          snippetEditorLayoutExpanded ? 'creator-content--editor-expanded' : '',
        ].filter(Boolean).join(' ')}
      >
        <div
          className={[
            'full-page-creator-shell',
            snippetEditorLayoutExpanded ? 'full-page-creator-shell--editor-expanded' : '',
          ].filter(Boolean).join(' ')}
        >
          <div className="full-page-creator-editor-column">
            <div className="full-page-creator-flashcard-wrap">
              {showSnippetEditorCard ? (
                <div
                  className={[
                    'full-page-creator-flashcard-card',
                    snippetEditorLayoutExpanded ? 'full-page-creator-flashcard-card--editor-expanded' : '',
                  ].filter(Boolean).join(' ')}
                >
                  <header className="full-page-creator-flashcard-header full-page-creator-flashcard-header--with-checklist">
                    <div className="full-page-creator-flashcard-header-top">
                      <div className="full-page-creator-flashcard-title-block">
                        <div className="full-page-creator-flashcard-title-row">
                          <DeckIcon className="full-page-creator-flashcard-title-icon" aria-hidden />
                          <h2 className="full-page-creator-flashcard-title">New snippet</h2>
                        </div>
                        <p className="full-page-creator-flashcard-subtitle">
                          Create a new code snippet to add to your cookbook.
                        </p>
                      </div>
                    </div>
                    {renderFlashcardCreationChecklist({
                      afterChecklistTrack: (
                        <div className="full-page-creator-creation-checklist-meta-row-leading">
                          <div className="full-page-creator-flashcard-snippet-language full-page-creator-creation-checklist-language-slot">
                            {renderFlashcardLanguageDropdown()}
                          </div>
                          <aside
                            className="full-page-creator-mode-rail full-page-creator-mode-rail--snippet-header-inline"
                            aria-label="Editor mode"
                            role="tablist"
                          >
                            <button
                              type="button"
                              role="tab"
                              aria-selected={editorMode === 'code'}
                              className={`full-page-creator-mode-rail__button ${editorMode === 'code' ? 'full-page-creator-mode-rail__button--active' : ''}`}
                              onClick={() => setEditorMode('code')}
                            >
                              <span className="full-page-creator-mode-rail__button-inner">
                                <FullPageCreatorRailCodeIcon className="full-page-creator-mode-rail__icon full-page-creator-mode-rail__icon--code" aria-hidden />
                                <span className="full-page-creator-mode-rail__label">Code</span>
                              </span>
                            </button>
                            <span className="full-page-creator-mode-tab-divider" aria-hidden="true" />
                            <button
                              type="button"
                              role="tab"
                              aria-selected={editorMode === 'notes'}
                              className={`full-page-creator-mode-rail__button ${editorMode === 'notes' ? 'full-page-creator-mode-rail__button--active' : ''}`}
                              onClick={() => setEditorMode('notes')}
                            >
                              <span className="full-page-creator-mode-rail__button-inner">
                                <FullPageCreatorRailNotesIcon className="full-page-creator-mode-rail__icon full-page-creator-mode-rail__icon--notes" aria-hidden />
                                <span className="full-page-creator-mode-rail__label">Notes</span>
                              </span>
                            </button>
                          </aside>
                        </div>
                      ),
                    })}
                  </header>

                  <div className="full-page-creator-flashcard-body">
                    {editorMode === 'code' ? (
                      <div
                        className={[
                          'full-page-creator-monaco-host',
                          snippetEditorExpanded ? 'full-page-creator-monaco-host--expanded' : '',
                        ].filter(Boolean).join(' ')}
                      >
                        <StudyModeCodeEditor
                          value={codeContent}
                          onChange={(value) => setCodeContent(value || '')}
                          language={language}
                          placeholder="Enter your code here..."
                          readOnly={false}
                          toolbarChrome
                          toolbarFilename={snippetToolbarFilename}
                          toolbarLanguageLabel={language}
                          toolbarLanguageDot={languageBrandDot}
                          expanded={snippetEditorExpanded}
                          onExpandClick={() => setSnippetEditorExpanded((v) => !v)}
                        />
                      </div>
                    ) : (
                      <div className="full-page-creator-notes-host">
                        <textarea
                          className="notes-textarea full-page-creator-notes-textarea"
                          value={notesContent}
                          onChange={(e) => setNotesContent(e.target.value)}
                          placeholder="Write your notes here..."
                        />
                      </div>
                    )}
                  </div>

                  <footer className="full-page-creator-flashcard-footer full-page-creator-flashcard-footer--snippet-editor">
                    {editorMode === 'code' ? (
                      <div className="full-page-creator-editor-footer-hint">
                        <span className="full-page-creator-editor-footer-hint-label">Format with</span>
                        <kbd className="full-page-creator-kbd">⇧</kbd>
                        <kbd className="full-page-creator-kbd">Alt</kbd>
                        <kbd className="full-page-creator-kbd">F</kbd>
                      </div>
                    ) : (
                      <div className="full-page-creator-editor-footer-hint full-page-creator-editor-footer-hint--muted">
                        <span className="full-page-creator-editor-footer-hint-label">Switch to Code to edit syntax-highlighted snippet.</span>
                      </div>
                    )}
                    <div className="full-page-creator-editor-footer-actions">
                      <button
                        type="button"
                        className="next-button full-page-creator-next-button full-page-creator-continue-button"
                        onClick={handleNext}
                        disabled={!codeContent.trim()}
                      >
                        Continue →
                      </button>
                      <button
                        type="button"
                        className="full-page-creator-cancel-button"
                        onClick={() => onClose?.()}
                      >
                        Cancel
                      </button>
                    </div>
                  </footer>
                </div>
              ) : (
                <div className="full-page-creator-flashcard-card full-page-creator-flashcard-card--form full-page-creator-flashcard-card--metadata-flow">
                  <header className="full-page-creator-flashcard-header full-page-creator-flashcard-header--with-checklist">
                    <div className="full-page-creator-flashcard-header-top">
                      <div className="full-page-creator-flashcard-title-block">
                        <div className="full-page-creator-flashcard-title-row">
                          <MetadataIcon className="full-page-creator-flashcard-title-icon" aria-hidden />
                          <h2 className="full-page-creator-flashcard-title">{metaCopy.title}</h2>
                        </div>
                        <p className="full-page-creator-flashcard-subtitle">{metaCopy.subtitle}</p>
                      </div>
                    </div>
                    {renderFlashcardCreationChecklist()}
                  </header>
                  <div className="full-page-creator-flashcard-body full-page-creator-flashcard-body--metadata">
                    {creationType === 'snippet'
                      ? renderSnippetForm({ hideFormTitle: true })
                      : renderCookbookCollectionForm({ hideFormTitle: true })}
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="full-page-creator-mode-rail-placeholder" aria-hidden="true" />
        </div>
      </div>
    </div>
  );
}

export default FullPageCreator;
